<!--
  @project E-School Web
  @author Vothana CHY
  @date 2026-07-13
-->

<script lang="ts" setup>
import { computed } from 'vue';
import { useSeoMeta } from '#imports';
import { useTermsLang } from '~/composables/useTermsLang';
import termsKh from '../../i18n/locales/terms_kh';
import termsEn from '../../i18n/locales/terms_en';

const { currentLang } = useTermsLang();

const terms = computed(() => {
  return currentLang.value === 'en' ? termsEn : termsKh;
});

useSeoMeta({
  title: () => terms.value.seo.title,
  description: () => terms.value.seo.description,
});
</script>

<template>
  <div
    class="min-h-screen bg-slate-900 font-sans text-slate-800 selection:bg-brand-red selection:text-white dark:text-slate-100"
  >
    <div class="relative flex min-h-screen flex-col overflow-x-hidden bg-white">
      <!-- Header -->
      <HomeHeader />

      <!-- Header Spacer -->
      <div class="h-[72px] w-full shrink-0 md:h-[124px]"></div>

      <!-- Page Hero Banner -->
      <section class="terms-hero">
        <div class="hero-overlay"></div>
        <div class="hero-content">
          <div class="hero-badge">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="20"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
              <polyline points="14 2 14 8 20 8"></polyline>
              <line x1="16" y1="13" x2="8" y2="13"></line>
              <line x1="16" y1="17" x2="8" y2="17"></line>
              <polyline points="10 9 9 9 8 9"></polyline>
            </svg>
            {{ terms.hero.badge }}
          </div>
          <h1 class="hero-title">{{ terms.hero.title }}</h1>
          <p class="hero-subtitle">{{ terms.hero.subtitle }}</p>
        </div>
      </section>

      <!-- Main Content -->
      <main class="terms-main">
        <div class="terms-container">
          <!-- Intro Card -->
          <div class="intro-card">
            <div class="intro-icon">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width="28"
                height="28"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="12" y1="8" x2="12" y2="12"></line>
                <line x1="12" y1="16" x2="12.01" y2="16"></line>
              </svg>
            </div>
            <p class="intro-text" v-html="terms.intro.text"></p>
          </div>

          <!-- Terms Sections -->
          <div class="terms-sections">
            <div v-for="section in terms.sections" :key="section.number" class="term-section">
              <div class="section-number">{{ section.number }}</div>
              <div class="section-body">
                <h2 class="section-title">{{ section.title }}</h2>
                <p v-for="(p, idx) in section.paragraphs || []" :key="idx" v-html="p"></p>

                <!-- Subsections -->
                <div
                  v-if="section.subsections && section.subsections.length"
                  class="subsections-list mt-4 flex flex-col gap-4"
                >
                  <div v-for="sub in section.subsections" :key="sub.num" class="subsection">
                    <h3 class="subsection-title">
                      <span class="subsection-num">{{ sub.num }}</span> {{ sub.title }}
                    </h3>
                    <p v-for="(subP, subIdx) in sub.paragraphs || []" :key="subIdx" v-html="subP"></p>

                    <!-- Nested Subsections -->
                    <div
                      v-if="sub.subsections && sub.subsections.length"
                      class="nested-subsections-list mt-3 flex flex-col gap-3"
                    >
                      <div v-for="nested in sub.subsections" :key="nested.num" class="nested-subsection">
                        <h4 class="nested-title">
                          <span class="nested-num">{{ nested.num }}</span> {{ nested.title }}
                        </h4>
                        <p v-for="(nestedP, nestedIdx) in nested.paragraphs" :key="nestedIdx" v-html="nestedP"></p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>

      <!-- Footer -->
      <HomeFooter />
    </div>
  </div>
</template>

<style scoped>
/* ─── Hero ─────────────────────────────────────────── */
.terms-hero {
  position: relative;
  background: linear-gradient(135deg, #0d3b2e 0%, #1a6647 50%, #0f4c35 100%);
  padding: 72px 24px 64px;
  text-align: center;
  overflow: hidden;
}

.terms-hero::before {
  content: '';
  position: absolute;
  inset: 0;
  background:
    radial-gradient(ellipse at 20% 50%, rgba(255, 255, 255, 0.06) 0%, transparent 60%),
    radial-gradient(ellipse at 80% 20%, rgba(255, 255, 255, 0.04) 0%, transparent 50%);
  pointer-events: none;
}

.hero-overlay {
  position: absolute;
  inset: 0;
  background: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23ffffff' fill-opacity='0.03'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")
    repeat;
  pointer-events: none;
}

.hero-content {
  position: relative;
  z-index: 1;
}

.hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: rgba(255, 255, 255, 0.12);
  backdrop-filter: blur(8px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  color: #a3f0cc;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 16px;
  border-radius: 999px;
  margin-bottom: 20px;
  letter-spacing: 0.04em;
}

.hero-title {
  font-size: clamp(28px, 5vw, 48px);
  font-weight: 800;
  color: #ffffff;
  margin: 0 0 12px;
  line-height: 1.2;
  text-shadow: 0 2px 20px rgba(0, 0, 0, 0.3);
}

.hero-subtitle {
  font-size: 15px;
  color: rgba(255, 255, 255, 0.65);
  margin: 0;
  letter-spacing: 0.02em;
}

/* ─── Main Layout ─────────────────────────────────── */
.terms-main {
  background: #f8fafb;
  padding: 56px 16px 80px;
  min-height: 60vh;
}

.terms-container {
  max-width: 860px;
  margin: 0 auto;
}

/* ─── Intro Card ──────────────────────────────────── */
.intro-card {
  background: linear-gradient(135deg, #e8f8f0 0%, #d4f0e2 100%);
  border: 1px solid #b2dfc7;
  border-left: 4px solid #1a6647;
  border-radius: 16px;
  padding: 28px 32px;
  margin-bottom: 40px;
  display: flex;
  gap: 20px;
  align-items: flex-start;
}

.intro-icon {
  flex-shrink: 0;
  width: 48px;
  height: 48px;
  background: linear-gradient(135deg, #1a6647, #28a060);
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  margin-top: 2px;
  box-shadow: 0 4px 12px rgba(26, 102, 71, 0.3);
}

.intro-text {
  color: #1e4d35;
  font-size: 15px;
  line-height: 1.85;
  margin: 0;
}

/* ─── Term Section Cards ──────────────────────────── */
.terms-sections {
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.term-section {
  background: #ffffff;
  border-radius: 16px;
  padding: 32px;
  display: flex;
  gap: 24px;
  box-shadow:
    0 1px 3px rgba(0, 0, 0, 0.06),
    0 4px 16px rgba(0, 0, 0, 0.04);
  border: 1px solid #eef2f7;
  transition:
    box-shadow 0.25s ease,
    transform 0.25s ease;
}

.term-section:hover {
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.08),
    0 12px 32px rgba(0, 0, 0, 0.06);
  transform: translateY(-2px);
}

.section-number {
  flex-shrink: 0;
  width: 44px;
  height: 44px;
  background: linear-gradient(135deg, #1a6647, #28a060);
  color: #fff;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 800;
  font-size: 17px;
  box-shadow: 0 4px 12px rgba(26, 102, 71, 0.25);
  margin-top: 2px;
}

.section-body {
  flex: 1;
  min-width: 0;
}

.section-title {
  font-size: 18px;
  font-weight: 700;
  color: #0d3b2e;
  margin: 0 0 14px;
  padding-bottom: 12px;
  border-bottom: 1px solid #e8f0ec;
  line-height: 1.4;
}

.section-body p {
  color: #374151;
  font-size: 15px;
  line-height: 1.9;
  margin: 0 0 14px;
  text-align: justify;
}

.section-body p:last-child {
  margin-bottom: 0;
}

/* ─── Subsections ─────────────────────────────────── */
.subsection {
  background: #f4fbf7;
  border-left: 3px solid #28a060;
  border-radius: 0 10px 10px 0;
  padding: 18px 20px;
  margin-top: 18px;
}

.subsection-title {
  font-size: 15px;
  font-weight: 700;
  color: #1a6647;
  margin: 0 0 10px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.subsection-num {
  background: rgba(26, 102, 71, 0.12);
  color: #1a6647;
  padding: 2px 8px;
  border-radius: 6px;
  font-size: 13px;
}

.subsection p {
  color: #374151;
  font-size: 14px;
  line-height: 1.85;
  margin: 0;
  text-align: justify;
}

/* ─── Nested subsections ──────────────────────────── */
.nested-subsection {
  background: #fff;
  border: 1px solid #d4eadd;
  border-radius: 10px;
  padding: 14px 18px;
  margin-top: 14px;
}

.nested-title {
  font-size: 14px;
  font-weight: 700;
  color: #0d3b2e;
  margin: 0 0 8px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.nested-num {
  background: #e0f5eb;
  color: #1a6647;
  padding: 1px 8px;
  border-radius: 5px;
  font-size: 12px;
  font-weight: 700;
}

.nested-subsection p {
  color: #4b5563;
  font-size: 14px;
  line-height: 1.85;
  margin: 0;
  text-align: justify;
}

/* ─── Responsive ──────────────────────────────────── */
@media (max-width: 600px) {
  .terms-hero {
    padding: 48px 16px 48px;
  }

  .intro-card {
    flex-direction: column;
    padding: 20px;
    gap: 14px;
  }

  .term-section {
    flex-direction: column;
    gap: 14px;
    padding: 22px 18px;
  }

  .section-number {
    width: 40px;
    height: 40px;
    font-size: 15px;
  }

  .section-title {
    font-size: 16px;
  }
}
</style>
